import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native-paper';

import { VisibilityIcon } from '@/assets';
import {
  hasSelectableGeoAreas,
  useGetGeoAreaTree
} from '@/features/api/hooks/shared-app/useGetGeoAreaTree';
import { ListItem } from '@/features/list/components/ListItem';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FormGeoAreaSelectListItem } from '@/shared/components/forms/FormGeoAreaSelectListItem';

type Props = {
  organizationId: string;
  depth?: number;
};

export const PostGeoAreaVisibilityField: FC<Props> = ({
  organizationId,
  depth
}) => {
  const { control } = useFormContext<PostFormValues>();
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor.organization'
  });
  const {
    data: geoAreaTree,
    isPending,
    isFetching,
    isError,
    refetch
  } = useGetGeoAreaTree(organizationId, depth);

  if (!geoAreaTree && (isPending || isFetching)) {
    return (
      <ListItem
        title={t('visibilityLabel')}
        icon={VisibilityIcon}
        badge={<ActivityIndicator size="small" />}
        hideRightArrow
      />
    );
  }

  if (isError && !geoAreaTree) {
    return (
      <ListItem
        title={t('visibilityLabel')}
        description={t('visibilityLoadError')}
        badge={t('visibilityRetry')}
        icon={VisibilityIcon}
        onPress={() => void refetch()}
      />
    );
  }

  return (
    <FormGeoAreaSelectListItem
      name="geoAreaIds"
      control={control}
      label={t('visibilityLabel')}
      organizationId={organizationId}
      depth={depth}
      icon={VisibilityIcon}
      editable={hasSelectableGeoAreas(geoAreaTree)}
      allPlacesLabel={(count) => t('visibilityAllPlaces', { count })}
    />
  );
};
